import type { CashSummary } from './cashSummaryQuery';
import type { CashSessionRow } from './cashSessionRow';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { fetchRemoteBranchCashSummary } from '../../reports/infra/reportsRemoteRepo';
import { getCashSummary } from './cashSummaryQuery';
import {
  CASH_SUMMARY_REMOTE_CACHE_MS,
  cashSummaryTodayStartIso,
  cashSummaryReportsCacheKey,
  clearCachedRemoteCashSummary,
  getCachedRemoteCashSummary,
  invalidateAllCashSummaryReportsCaches,
  invalidateCashSummaryCaches,
  setCachedRemoteCashSummary,
} from './cashSummaryRemoteCache';

export {
  CASH_SUMMARY_REMOTE_CACHE_MS,
  cashSummaryTodayStartIso,
  cashSummaryReportsCacheKey,
  clearCachedRemoteCashSummary,
  getCachedRemoteCashSummary,
  invalidateAllCashSummaryReportsCaches,
  invalidateCashSummaryCaches,
  setCachedRemoteCashSummary,
} from './cashSummaryRemoteCache';

export type CashSummaryRefreshOptions = { force?: boolean };

/** Probe remoto cuando no hay sesión local y hay red (multi-dispositivo). */
export function shouldForceCashSummaryProbe(local: CashSummary, online: boolean): boolean {
  return !local.activeSession && online;
}

/** Soft por defecto; force explícito o probe remoto si no hay sesión local (multi-dispositivo). */
export async function resolveCashSummaryRefreshOptions(
  local: CashSummary,
  explicit?: CashSummaryRefreshOptions
): Promise<CashSummaryRefreshOptions | undefined> {
  if (explicit?.force) return { force: true };
  if (shouldForceCashSummaryProbe(local, await isOnline())) {
    return { force: true };
  }
  return undefined;
}

/** Web online: el servidor manda el turno activo (sin merge que priorice espejo local). */
export function cashSummaryFromRemoteAuthority(remote: CashSummary): CashSummary {
  const active =
    remote.activeSession?.status === 'open' ? remote.activeSession : null;
  return {
    activeSession: active,
    todaySalesCount: remote.todaySalesCount,
    todaySalesTotal: remote.todaySalesTotal,
    todayCashTotal: remote.todayCashTotal,
    todayDigitalTotal: remote.todayDigitalTotal,
    sessionCashTotal: active ? remote.sessionCashTotal : 0,
    sessionSalesCount: active ? remote.sessionSalesCount : 0,
    sessionSalesTotal: active ? remote.sessionSalesTotal : 0,
    sessionTipsTotal: active ? remote.sessionTipsTotal : 0,
    sessionPaymentBreakdown: active ? remote.sessionPaymentBreakdown : [],
    sessionOutflowsTotal: active ? remote.sessionOutflowsTotal : 0,
    sessionInflowsTotal: active ? remote.sessionInflowsTotal : 0,
    lastClosedSession: remote.lastClosedSession,
    expectedBalance: active ? (remote.expectedBalance ?? null) : null,
  };
}

/** Resumen de caja: online = remoto (server-authoritative); offline = local. */
export async function resolveCashSummary(
  branchId: string,
  options?: CashSummaryRefreshOptions
): Promise<CashSummary> {
  const local = await getCashSummary(branchId);
  // Si no se puede determinar conectividad, tratar como offline (fail-closed).
  const online = await isOnline().catch(() => false);
  if (!online) return { ...local, remoteStatus: 'offline' };

  if (!options?.force) {
    const cached = getCachedRemoteCashSummary(branchId);
    if (cached) {
      return { ...cashSummaryFromRemoteAuthority(cached), remoteStatus: 'ok' };
    }
  }

  let remote: CashSummary | null = null;
  try {
    remote = await fetchRemoteBranchCashSummary(branchId, cashSummaryTodayStartIso(), {
      force: options?.force,
    });
  } catch {
    remote = null;
  }
  if (remote) {
    setCachedRemoteCashSummary(branchId, remote);
    return { ...cashSummaryFromRemoteAuthority(remote), remoteStatus: 'ok' };
  }

  // El stub local siempre reporta "sin sesión": propagar remoteStatus para que
  // los callers distingan "server dice sin turno" de "no se pudo verificar".
  return { ...local, remoteStatus: 'unavailable' };
}

/** Sesión activa fusionada (local + remoto), alineada con Caja y turnos. */
export async function getMergedActiveCashSessionId(
  branchId: string,
  options?: CashSummaryRefreshOptions
): Promise<string | null> {
  const summary = await resolveCashSummary(branchId, options);
  return summary.activeSession?.id ?? null;
}

/** Sesión activa fusionada (fila completa con openedAt + openingAmount). */
export async function getMergedActiveCashSession(
  branchId: string,
  options?: CashSummaryRefreshOptions
): Promise<CashSessionRow | null> {
  const summary = await resolveCashSummary(branchId, options);
  return summary.activeSession ?? null;
}
